// src/BadgeManagement.js
// Badge Management: two tabs — History (live badge scans) and Cardholders
// (add/edit/delete TX3 cards). Gated by the OP14 "Badge Management" function.
// Scan data comes from the /api/badge-management endpoints.
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import ProtectedComponent from './ProtectedComponent';
import TX3CardManager from './TX3CardManager';
import ScanHistoryModal from './ScanHistoryModal';
import { getSocket } from './socket';
import './styles/BadgeManagement.css';

// Compact page window: first, last, and a few pages around the current one,
// with '…' gaps. (Same behavior as the Door Functions scan history pager.)
function getPageWindow(current, total) {
  if (total <= 1) return [1];
  const delta = 2;
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);
  const pages = [1];
  if (left > 2) pages.push('…');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('…');
  pages.push(total);
  return pages;
}

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function BadgeManagement() {
  const [scans, setScans] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [employees, setEmployees] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [jumpPage, setJumpPage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('history');
  const [histScan, setHistScan] = useState(null); // scan row whose card history is shown
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchScans = useCallback(async (page = 1, employee = '', start = '', end = '') => {
    setLoading(true);
    setError(null);
    try {
      let url = `/badge-management?page=${page}&limit=50`;
      if (employee) url += `&employee=${encodeURIComponent(employee)}`;
      if (start) url += `&startDate=${start}`;
      if (end) url += `&endDate=${end}`;
      const { data } = await axiosInstance.get(url);
      setScans(data.scans || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Error fetching scan history:', err);
      setError(err.response?.data?.message || 'Failed to load scan history.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/badge-management/employees');
      setEmployees(data.employees || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, []);

  useEffect(() => {
    fetchScans(1, '');
    fetchEmployees();
  }, [fetchScans, fetchEmployees]);

  // Live updates: prepend each new scan as it happens (only on the newest page
  // with no filter, so we don't disturb paging/filtering). The server emits
  // 'badgeScan' on every recorded swipe — from the serial service or the log
  // fallback alike.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;
    const onScan = (s) => {
      if (pagination.page !== 1 || employeeFilter || startDate || endDate) return;
      setScans((prev) => [{
        _id: `live-${s.timestamp}-${s.cardNumber}-${Math.random().toString(36).slice(2)}`,
        timestamp: s.timestamp,
        employeeName: s.employeeName,
        badgeId: s.badgeId,
        cardNumber: s.cardNumber,
        direction: s.direction,
        door: s.door,
        accessGranted: s.accessGranted,
      }, ...prev].slice(0, 50));
      setPagination((p) => ({ ...p, total: (p.total || 0) + 1 }));
    };
    socket.on('badgeScan', onScan);
    return () => socket.off('badgeScan', onScan);
  }, [pagination.page, employeeFilter, startDate, endDate]);

  // Jump to a page, clamped; no-op if it's the page already shown.
  const goToPage = (p) => {
    const target = Math.max(1, Math.min(pagination.pages, Number(p) || 1));
    if (target === pagination.page) return;
    fetchScans(target, employeeFilter, startDate, endDate);
  };

  // Apply the current employee + date filters from page 1.
  const applyFilters = (nextEmployee = employeeFilter, nextStart = startDate, nextEnd = endDate) => {
    fetchScans(1, nextEmployee, nextStart, nextEnd);
  };

  return (
    <ProtectedComponent functionCode="OP14" functionName="Badge Management">
      <div className="badge-management">
        <h1 className="bsh-title">Badge Management</h1>
        <div className="bsh-tabs">
          <button
            className={`bsh-tab ${tab === 'history' ? 'active' : ''}`}
            onClick={() => setTab('history')}
          >
            History
          </button>
          <button
            className={`bsh-tab ${tab === 'cardholders' ? 'active' : ''}`}
            onClick={() => setTab('cardholders')}
          >
            Cardholders
          </button>
        </div>

        {tab === 'cardholders' && (
          <div className="bsh-tabpanel">
            <TX3CardManager />
          </div>
        )}

        {tab === 'history' && (
        <>
        <div className="bsh-header">
          <div className="bsh-controls">
            <select
              className="bsh-filter"
              value={employeeFilter}
              onChange={(e) => {
                setEmployeeFilter(e.target.value);
                applyFilters(e.target.value, startDate, endDate);
              }}
            >
              <option value="">All Users</option>
              {employees.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <label className="bsh-datefld">From
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); applyFilters(employeeFilter, e.target.value, endDate); }}
              />
            </label>
            <label className="bsh-datefld">To
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); applyFilters(employeeFilter, startDate, e.target.value); }}
              />
            </label>
            {(startDate || endDate) && (
              <button
                className="bsh-refresh"
                onClick={() => { setStartDate(''); setEndDate(''); applyFilters(employeeFilter, '', ''); }}
              >
                Clear dates
              </button>
            )}
            <button
              className="bsh-refresh"
              onClick={() => fetchScans(pagination.page, employeeFilter, startDate, endDate)}
            >
              Refresh
            </button>
            <span className="bsh-total">{pagination.total.toLocaleString()} scans</span>
          </div>
        </div>

        {error ? (
          <div className="bsh-error">{error}</div>
        ) : loading ? (
          <div className="bsh-loading">Loading scan history…</div>
        ) : scans.length === 0 ? (
          <p className="bsh-empty">No scans recorded yet.</p>
        ) : (
          <>
            <div className="bsh-table-wrap">
              <table className="bsh-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Name</th>
                  <th>Badge ID</th>
                  <th>Direction</th>
                  <th>Door</th>
                  <th>Access</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((scan) => (
                  <tr key={scan._id} className={scan.accessGranted ? 'access-granted' : 'access-denied'}>
                    <td>{formatTime(scan.timestamp)}</td>
                    <td>
                      <button
                        type="button"
                        className="bsh-namebtn"
                        onClick={() => setHistScan(scan)}
                        title="View this card's scan history"
                      >
                        {scan.employeeName || '(Unknown)'}
                      </button>
                    </td>
                    <td className="badge-id">{scan.badgeId}</td>
                    <td>
                      <span className={`direction ${scan.direction}`}>
                        {scan.direction === 'in' ? 'IN' : scan.direction === 'out' ? 'OUT' : '—'}
                      </span>
                    </td>
                    <td>{scan.door}</td>
                    <td>
                      <span className={scan.accessGranted ? 'granted' : 'denied'}>
                        {scan.accessGranted ? 'GRANTED' : 'DENIED'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
              >
                Previous
              </button>

              <div className="page-numbers">
                {getPageWindow(pagination.page, pagination.pages).map((p, i) =>
                  p === '…' ? (
                    <span key={`gap-${i}`} className="page-ellipsis">…</span>
                  ) : (
                    <button
                      key={p}
                      className={p === pagination.page ? 'page-num active' : 'page-num'}
                      aria-current={p === pagination.page ? 'page' : undefined}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
              </div>

              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => goToPage(pagination.page + 1)}
              >
                Next
              </button>

              <span className="page-jump">
                Go to
                <input
                  type="number"
                  min={1}
                  max={pagination.pages}
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && jumpPage) {
                      goToPage(jumpPage);
                      setJumpPage('');
                    }
                  }}
                  placeholder={`1–${pagination.pages}`}
                  title="Type a page number and press Enter to jump"
                />
              </span>
            </div>
          </>
        )}
        </>
        )}

        {histScan && (
          <ScanHistoryModal
            title={histScan.employeeName || '(Unknown)'}
            cardNumber={histScan.cardNumber}
            onClose={() => setHistScan(null)}
          />
        )}
      </div>
    </ProtectedComponent>
  );
}

export default BadgeManagement;
